import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Description as DocxIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import mammoth from 'mammoth';

interface DOCXViewerProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileSize?: number;
}

const DOCXViewer: React.FC<DOCXViewerProps> = ({
  open,
  onClose,
  fileUrl,
  fileName,
  fileSize,
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Reset state when component opens
  useEffect(() => {
    if (open) {
      setHtmlContent('');
      setError(null);
      setFullscreen(false);
      setRetryCount(0);
      loadDocument();
    }
  }, [open, fileUrl]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Loading DOCX document from:', fileUrl);
      
      // Fetch the document
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      console.log('Document fetched, size:', arrayBuffer.byteLength, 'bytes');

      // Convert DOCX to HTML using mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer });
      
      if (result.messages && result.messages.length > 0) {
        console.warn('Mammoth conversion messages:', result.messages);
      }

      setHtmlContent(result.value);
      console.log('DOCX converted to HTML successfully');
      
    } catch (error) {
      console.error('Error loading DOCX document:', error);
      setError('Error loading Word document: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading DOCX:', error);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    loadDocument();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : "lg"}
      fullWidth={!fullscreen}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          borderRadius: fullscreen ? 0 : 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          maxHeight: fullscreen ? '100vh' : '90vh',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                color: 'white',
              }}>
                <DocxIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {fileName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Word Document"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {fileSize && (
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(fileSize)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Refresh Document">
                <IconButton
                  onClick={handleRetry}
                  disabled={loading}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { 
                      background: 'rgba(0,0,0,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton
                  onClick={() => setFullscreen(!fullscreen)}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { 
                      background: 'rgba(0,0,0,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} sx={{ 
                color: 'text.secondary',
                '&:hover': { 
                  background: 'rgba(0,0,0,0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: 'rgba(255, 255, 255, 0.6)' }}>
          {error ? (
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  retryCount < 3 ? (
                    <Button color="inherit" size="small" onClick={handleRetry}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {error}
                {retryCount >= 3 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Maximum retry attempts reached. Please try downloading the file instead.
                  </Typography>
                )}
              </Alert>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  py: 8,
                  gap: 2
                }}>
                  <CircularProgress size={40} />
                  <Typography variant="body1">
                    Loading Word document...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Converting DOCX to HTML
                  </Typography>
                </Box>
              ) : htmlContent ? (
                <Paper sx={{ 
                  p: 3, 
                  background: 'white',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)',
                  maxHeight: fullscreen ? '70vh' : '60vh',
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#a8a8a8',
                    },
                  },
                }}>
                  <Box
                    sx={{
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        color: '#1976d2',
                        marginTop: 2,
                        marginBottom: 1,
                        fontWeight: 'bold',
                      },
                      '& p': {
                        marginBottom: 1,
                        lineHeight: 1.6,
                      },
                      '& ul, & ol': {
                        marginBottom: 1,
                        paddingLeft: 2,
                      },
                      '& li': {
                        marginBottom: 0.5,
                      },
                      '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginBottom: 2,
                        '& th, & td': {
                          border: '1px solid #ddd',
                          padding: '8px',
                          textAlign: 'left',
                        },
                        '& th': {
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                        },
                      },
                      '& blockquote': {
                        borderLeft: '4px solid #1976d2',
                        paddingLeft: 2,
                        marginLeft: 0,
                        fontStyle: 'italic',
                        color: '#666',
                      },
                      '& strong, & b': {
                        fontWeight: 'bold',
                      },
                      '& em, & i': {
                        fontStyle: 'italic',
                      },
                      '& u': {
                        textDecoration: 'underline',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                </Paper>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  py: 8,
                  gap: 2
                }}>
                  <Typography variant="body1" color="text.secondary">
                    No content to display
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Download DOCX
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default DOCXViewer;
